import type {
  NativeReportColumn,
  NativeReportResponse,
  NativeReportRow,
} from '@/api/reports';

import { isAggregationColumn } from './nativeDataTransforms';

/**
 * Zero is a real answer for a tally or a total — "no deals" is 0, not
 * "unknown". For an average, a minimum, or a maximum over an empty set there
 * is genuinely no value, so those render the empty glyph instead of a
 * fabricated 0.
 *
 * The column's `aggregation` names the function. Aliases are free-form
 * ("deal_count", "total_amount"), so the alias is only read when a response
 * predates that field — then the default `count` / `<aggregation>_<property>`
 * encoding is the best available guess.
 */
export function zeroIsMeaningful(
  column: Pick<NativeReportColumn, 'key' | 'aggregation'>
): boolean {
  const aggregation = column.aggregation ?? column.key.split('_')[0];
  return aggregation === 'count' || aggregation === 'sum';
}

/**
 * The data a metric tile's CSV export contains.
 *
 * A count or sum that matched nothing renders "0" on the tile, but the
 * response carries no row for it — so the export would otherwise refuse a
 * tile that plainly shows a number. This supplies that one row, holding 0 for
 * each count/sum column, so the file matches the tile. A metric with no
 * zero-meaningful column (an average over nothing) stays empty: the tile shows
 * no value, and neither does the file.
 */
export function metricExportData(
  data: NativeReportResponse
): NativeReportResponse {
  if (data.rows.length > 0) {
    return data;
  }
  const zeroColumns = data.columns.filter(
    column => isAggregationColumn(column) && zeroIsMeaningful(column)
  );
  if (zeroColumns.length === 0) {
    return data;
  }
  const row: NativeReportRow = Object.fromEntries(
    zeroColumns.map(column => [column.key, 0])
  );
  return { ...data, rows: [row], total_count: 1 };
}
